import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert, Container, CircularProgress, keyframes, InputAdornment, IconButton, CheckCircleIcon, XCircleIcon } from '@mui/material';
import { LockIcon, EyeIcon, EyeOffIcon, ShieldAlertIcon } from 'lucide-react';
import { createAuditLog } from '../../utils/auditLog';
import { hashPassword } from '../../utils/passwordUtils';
import { generateDeviceFingerprint } from '../../utils/deviceFingerprint';
import { useAuth } from '../../context/AuthContext';
const floatingAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const ForcePasswordChangePage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const userData = location.state?.userData;
  const trustDevice = location.state?.trustDevice || false;
  useEffect(() => {
    if (!userData) {
      navigate('/login');
    }
  }, [userData, navigate]);
  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    const minLength = value.length >= 8;
    const maxLength = value.length <= 15;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
    if (!minLength || !maxLength) {
      return 'Password must be between 8-15 characters';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecial) {
      return 'Password must contain at least one special character';
    }
    return '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newPasswordError = validatePassword(newPassword);
    const confirmPasswordError = newPassword !== confirmPassword ? 'Passwords do not match' : '';
    if (newPasswordError || confirmPasswordError) {
      setErrors({
        newPassword: newPasswordError,
        confirmPassword: confirmPasswordError
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    try {
      const hashedNewPassword = hashPassword(newPassword);
      // Update user password in localStorage users collection
      const storedUsersData = localStorage.getItem('users');
      if (storedUsersData) {
        const users = JSON.parse(storedUsersData);
        const updatedUsers = users.map((user: any) => {
          if (user.id === userData.id) {
            return {
              ...user,
              password: hashedNewPassword,
              firstLoginRequired: false,
              passwordChangedAt: new Date().toISOString()
            };
          }
          return user;
        });
        localStorage.setItem('users', JSON.stringify(updatedUsers));
      }
      // Also update in branchUsers if it exists
      const storedBranchUsers = localStorage.getItem('branchUsers');
      if (storedBranchUsers) {
        const branchUsers = JSON.parse(storedBranchUsers);
        const updatedBranchUsers = branchUsers.map((user: any) => {
          if (user.id === userData.id) {
            return {
              ...user,
              hashedOtp: hashedNewPassword,
              password: hashedNewPassword,
              firstLoginRequired: false,
              passwordChangedAt: new Date().toISOString()
            };
          }
          return user;
        });
        localStorage.setItem('branchUsers', JSON.stringify(updatedBranchUsers));
      }
      // If user selected "Trust This Device" during OTP, save it now after password change
      if (trustDevice) {
        const deviceFingerprint = generateDeviceFingerprint();
        const trustedDevices = JSON.parse(localStorage.getItem('trustedDevices') || '{}');
        if (!trustedDevices[userData.id]) {
          trustedDevices[userData.id] = [];
        }
        // Check if device already exists
        const deviceExists = trustedDevices[userData.id].some((device: any) => device.fingerprint === deviceFingerprint);
        if (!deviceExists) {
          const deviceMetadata = {
            fingerprint: deviceFingerprint,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenResolution: `${screen.width}x${screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            deviceName: `${navigator.platform} - ${navigator.userAgent.split('(')[1]?.split(')')[0] || 'Unknown'}`
          };
          trustedDevices[userData.id].push(deviceMetadata);
          localStorage.setItem('trustedDevices', JSON.stringify(trustedDevices));
          createAuditLog({
            userId: userData.id,
            eventType: 'Status Change',
            source: 'SYSTEM',
            changes: [{
              fieldName: 'Trusted Device',
              oldValue: null,
              newValue: `Device added: ${deviceMetadata.deviceName}`
            }]
          });
        }
      }
      // Update current user session
      const updatedUserData = {
        ...userData,
        firstLoginRequired: false,
        password: hashedNewPassword,
        passwordChangedAt: new Date().toISOString()
      };
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      // Create audit log
      createAuditLog({
        userId: userData.id,
        eventType: 'Status Change',
        source: 'BO_USER',
        changes: [{
          fieldName: 'Password',
          oldValue: 'Temporary OTP',
          newValue: 'User-defined password'
        }, {
          fieldName: 'firstLoginRequired',
          oldValue: 'true',
          newValue: 'false'
        }]
      });
      // Show success message
      setSuccess('Password changed successfully! You will be logged out in 2 seconds...');
      // Clear user session and logout after 2 seconds
      setTimeout(() => {
        logout(); // Use AuthContext logout to properly clear authentication state
        navigate('/login', {
          replace: true
        }); // Use replace to prevent back navigation
      }, 2000);
    } catch (error) {
      setErrors({
        newPassword: 'An error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    position: 'relative',
    px: 2,
    overflow: 'hidden'
  }} data-id="element-104">
      <Container maxWidth="sm" sx={{
      mb: 4,
      mt: 4,
      position: 'relative',
      zIndex: 1
    }} data-id="element-105">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3
      }} data-id="element-106">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 80,
          marginBottom: 20
        }} data-id="element-107" />
          <Typography variant="subtitle1" sx={{
          color: '#6B7280',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '1px'
        }} data-id="element-108">
            Security Setup
          </Typography>
        </Box>

        <Paper elevation={0} sx={{
        p: {
          xs: 3,
          sm: 4
        },
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        position: 'relative',
        animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
      }} data-id="element-109">
          <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2
        }} data-id="element-110">
            <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#E8F0FE',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }} data-id="element-111">
              <ShieldAlertIcon size={32} color="#0052CC" data-id="element-112" />
            </Box>
          </Box>

          <Typography variant="h5" component="h2" sx={{
          mb: 1,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-113">
            Update Password
          </Typography>

          <Typography variant="body2" sx={{
          mb: 3,
          textAlign: 'center',
          color: 'text.secondary'
        }} data-id="element-114">
            Please set a new password for your account
          </Typography>

          {success ? <Alert severity="success" sx={{
          mb: 3
        }} data-id="element-115">
              {success}
            </Alert> : <Alert severity="info" sx={{
          mb: 3
        }} data-id="element-116">
              <Typography variant="caption" data-id="element-117">
                Password must be 8-15 characters with uppercase, lowercase,
                number, and special character.
              </Typography>
            </Alert>}

          <form onSubmit={handleSubmit} data-id="element-118">
            <TextField id="newPassword" label="New Password" type={showNewPassword ? 'text' : 'password'} variant="outlined" fullWidth value={newPassword} onChange={e => setNewPassword(e.target.value)} error={!!errors.newPassword} helperText={errors.newPassword} disabled={isLoading || !!success} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-120">
                    <LockIcon size={20} color="#0052CC" data-id="element-121" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-122">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" disabled={isLoading || !!success} data-id="element-123">
                      {showNewPassword ? <EyeOffIcon size={20} data-id="element-124" /> : <EyeIcon size={20} data-id="element-125" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-119" />

            <TextField id="confirmPassword" label="Confirm Password" type={showConfirmPassword ? 'text' : 'password'} variant="outlined" fullWidth value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} error={!!errors.confirmPassword} helperText={errors.confirmPassword} disabled={isLoading || !!success} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-127">
                    <LockIcon size={20} color="#0052CC" data-id="element-128" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-129">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" disabled={isLoading || !!success} data-id="element-130">
                      {showConfirmPassword ? <EyeOffIcon size={20} data-id="element-131" /> : <EyeIcon size={20} data-id="element-132" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 3
          }} data-id="element-126" />

            <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || !!success || !newPassword || !confirmPassword} sx={{
            py: 1.5,
            mt: 2,
            borderRadius: '6px',
            backgroundColor: '#0052CC',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#003DA8',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
            }
          }} data-id="element-133">
              {isLoading ? <CircularProgress size={24} sx={{
              color: 'white'
            }} data-id="element-134" /> : 'Change Password'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>;
};
export default ForcePasswordChangePage;